/**
 * Directive-mention parser
 *
 * The composer (LexicalComposerInput + the trigger-popover pipeline) writes
 * mentions into the user's text using `:type[label]{name=id}` syntax
 * (the assistant-ui default directive formatter). Three mention types exist:
 *  - `surface` — id is the composite `${canvasId}:${surfaceId}`, recovered
 *    via `parseSurfaceRefId`. Trigger char: `#`.
 *  - `agent`   — id is the agent's own prefixed id (`agt-…`). Trigger char: `@`.
 *  - `file`    — label is its filename. Trigger char: `!`. The id encodes
 *    where the file lives: a conversation-VFS file's id is just its path,
 *    while a knowledge-base file's id is `${kbId}::${path}` (recovered via
 *    `parseFileRefId`). The agent reads the file via that path within the
 *    resolved owner (the conversation VFS, or the KB named by `kbId`).
 *
 * `extractMentions(text)` returns:
 *  - `message`: text with each mention substituted by its `label`, so the
 *    agent reads natural prose. Agent mentions are prefixed with `@` so the
 *    coordinator LLM still sees the user's intent (`@Jira Expert`); file
 *    mentions are prefixed with `!` so the LLM can see the user referenced
 *    a specific file (the structured `fileRefs` field carries the path).
 *  - `surfaceRefs` / `agentRefs` / `fileRefs`: one entry per parsed mention,
 *    in document order. Empty when no mentions of that type are present.
 */

import { unstable_defaultDirectiveFormatter } from '@assistant-ui/core';
import type { AgentRef, FileRef, SurfaceRef } from '../types';

export interface ExtractedMentions {
  message: string;
  surfaceRefs: SurfaceRef[];
  agentRefs: AgentRef[];
  fileRefs: FileRef[];
}

export const MENTION_TYPE_SURFACE = 'surface';
export const MENTION_TYPE_AGENT = 'agent';
export const MENTION_TYPE_FILE = 'file';

export function extractMentions(text: string): ExtractedMentions {
  const segments = unstable_defaultDirectiveFormatter.parse(text);
  const surfaceRefs: SurfaceRef[] = [];
  const agentRefs: AgentRef[] = [];
  const fileRefs: FileRef[] = [];
  let out = '';
  for (const seg of segments) {
    if (seg.kind === 'text') {
      out += seg.text;
      continue;
    }
    if (seg.type === MENTION_TYPE_SURFACE) {
      const ref = parseSurfaceRefId(seg.id);
      if (ref) surfaceRefs.push(ref);
      out += seg.label;
      continue;
    }
    if (seg.type === MENTION_TYPE_AGENT) {
      agentRefs.push({ agentId: seg.id, agentName: seg.label });
      // Re-prepend `@` for natural-language reading, and inline the agentId
      // so the coordinator LLM can resolve the target unambiguously even
      // when two agents share a similar name. The structured `agentRefs`
      // field carries the same data for future backend routing, but until
      // that wiring lands the LLM only sees this text.
      out += `@${seg.label} (${seg.id})`;
      continue;
    }
    if (seg.type === MENTION_TYPE_FILE) {
      // id encodes the owner: a bare path → conversation VFS; `${kbId}::${path}`
      // → a knowledge-base file. label = filename. The structured `fileRefs`
      // entry carries the unambiguous path (+ kbId when KB-scoped); the prose
      // form gives the LLM a readable cue along with the path in parentheses,
      // qualified by the KB id when the file lives outside the conversation,
      // so it can reference the right file even when two files share a name.
      const { kbId, path } = parseFileRefId(seg.id);
      fileRefs.push({ path, name: seg.label, ...(kbId && { kbId }) });
      out += kbId
        ? `!${seg.label} (${path} in knowledge base ${kbId})`
        : `!${seg.label} (${path})`;
      continue;
    }
    // Unknown directive — keep label so the agent still sees something
    // intelligible rather than dropped content.
    out += seg.label;
  }
  return { message: out, surfaceRefs, agentRefs, fileRefs };
}

/**
 * The id encoded by the composer for surface mentions is
 * `${canvasId}:${surfaceId}`. Both ids are prefixed (`cvs-…`, `srf-…`),
 * so a single colon split is unambiguous.
 */
export function parseSurfaceRefId(id: string): SurfaceRef | null {
  const idx = id.indexOf(':');
  if (idx <= 0 || idx === id.length - 1) return null;
  const canvasId = id.slice(0, idx);
  const surfaceId = id.slice(idx + 1);
  if (!canvasId || !surfaceId) return null;
  return { canvasId, surfaceId };
}

export function buildSurfaceRefId(canvasId: string, surfaceId: string): string {
  return `${canvasId}:${surfaceId}`;
}

/**
 * File-mention id encoding. A conversation-VFS file keeps `id === path`
 * (unchanged — preserves back-compat with already-rendered history). A
 * knowledge-base file packs its owning KB into the id as `${kbId}::${path}`.
 *
 * `parseFileRefId` only treats an id as KB-scoped when it starts with the
 * `kbs-` prefix and contains the `::` delimiter, so a legacy conversation
 * path (which may contain a single `:` but never `kbs-…::`) is never
 * misread as a KB ref.
 */
export function buildFileRefId(kbId: string | undefined, path: string): string {
  return kbId ? `${kbId}::${path}` : path;
}

export function parseFileRefId(id: string): { kbId?: string; path: string } {
  if (id.startsWith('kbs-')) {
    const idx = id.indexOf('::');
    if (idx > 0) return { kbId: id.slice(0, idx), path: id.slice(idx + 2) };
  }
  return { path: id };
}
